/*
  Alps Goodness — Global Theme JavaScript
  File: assets/alps-goodness.js
  Paste into: Online Store > Themes > Edit Code > assets/alps-goodness.js
  Then add to your theme.liquid: {{ 'alps-goodness.js' | asset_url | script_tag }}
  OR use: <script src="{{ 'alps-goodness.js' | asset_url }}" defer></script>
  Place just before the closing </body> tag in theme.liquid
*/

(function() {
  'use strict';

  /* ── Scroll-triggered fade-in ───────────────────────────────────────────── */
  function initScrollAnimations() {
    const els = document.querySelectorAll('.ag-fade-in');
    if (!els.length) return;

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -60px 0px', threshold: 0.1 });

      els.forEach(el => io.observe(el));
    } else {
      // Fallback: show all immediately
      els.forEach(el => el.classList.add('is-visible'));
    }
  }

  /* ── Toast notification ─────────────────────────────────────────────────── */
  window.agShowToast = function(message, duration = 3000) {
    let toast = document.querySelector('.ag-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'ag-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => toast.classList.remove('is-visible'), duration);
  };

  /* ── Cart count badge (global) ──────────────────────────────────────────── */
  function updateAllCartCounts(count) {
    document.querySelectorAll('.ag-bottom-bar__cart-count, [data-cart-count]').forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? '' : 'none';
    });
  }

  function fetchCartCount() {
    fetch('/cart.js')
      .then(r => r.json())
      .then(data => updateAllCartCounts(data.item_count))
      .catch(() => {});
  }

  document.addEventListener('cart:refresh', fetchCartCount);
  document.addEventListener('cart:updated', fetchCartCount);

  /* ── Add to Cart (AJAX) ─────────────────────────────────────────────────── */
  document.addEventListener('submit', async function(e) {
    const form = e.target;
    if (!form.action || !form.action.includes('/cart/add')) return;

    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const originalHTML = btn ? btn.innerHTML : '';
    const id = form.querySelector('[name="id"]')?.value;
    const qty = form.querySelector('[name="quantity"]')?.value || 1;

    if (!id) return;

    if (btn) {
      btn.classList.add('is-adding');
      btn.innerHTML = 'Adding…';
      btn.disabled = true;
    }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: parseInt(id), quantity: parseInt(qty) })
      });

      if (!res.ok) throw new Error('Cart add failed');

      if (btn) {
        btn.classList.remove('is-adding');
        btn.classList.add('is-added');
        btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7l4 4 6-8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg> Added!';
        setTimeout(() => {
          btn.classList.remove('is-added');
          btn.innerHTML = originalHTML;
          btn.disabled = false;
        }, 2000);
      }

      document.dispatchEvent(new CustomEvent('cart:refresh'));
      window.agShowToast('Added to your bag! 🛍');

    } catch (err) {
      if (btn) {
        btn.classList.remove('is-adding');
        btn.innerHTML = originalHTML;
        btn.disabled = false;
      }
      window.agShowToast('Something went wrong. Please try again.');
    }
  });

  /* ── Wishlist (localStorage) ────────────────────────────────────────────── */
  function getWishlist() {
    try { return JSON.parse(localStorage.getItem('ag_wishlist') || '[]'); } catch(e) { return []; }
  }
  function saveWishlist(list) {
    try { localStorage.setItem('ag_wishlist', JSON.stringify(list)); } catch(e) {}
  }

  function initWishlistBtns() {
    const list = getWishlist();
    document.querySelectorAll('.ag-product-card__wish').forEach(btn => {
      const card = btn.closest('.ag-product-card');
      const productId = card?.dataset.productId;
      if (productId && list.includes(productId)) btn.classList.add('is-wished');

      btn.addEventListener('click', function() {
        const id = this.closest('.ag-product-card')?.dataset.productId;
        if (!id) { this.classList.toggle('is-wished'); return; }
        const wl = getWishlist();
        const idx = wl.indexOf(id);
        if (idx > -1) {
          wl.splice(idx, 1);
          this.classList.remove('is-wished');
          window.agShowToast('Removed from wishlist');
        } else {
          wl.push(id);
          this.classList.add('is-wished');
          window.agShowToast('Added to wishlist ♥');
        }
        saveWishlist(wl);
      });
    });
  }

  /* ── Lazy load images (native + polyfill) ───────────────────────────────── */
  function initLazyImages() {
    if ('loading' in HTMLImageElement.prototype) return; // Native support

    const images = document.querySelectorAll('img[loading="lazy"]');
    if (!images.length) return;
    const io = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const img = entry.target;
          if (img.dataset.src) img.src = img.dataset.src;
          io.unobserve(img);
        }
      });
    }, { rootMargin: '200px' });
    images.forEach(img => io.observe(img));
  }

  /* ── Announcement bar scroll hide ──────────────────────────────────────── */
  function initAnnBarScroll() {
    const bar = document.getElementById('ag-announcement-bar');
    if (!bar) return;
    let lastY = 0;
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      // Only hide after scrolling 80px down
      if (y > 80 && y > lastY) {
        bar.classList.add('is-hidden');
      } else {
        bar.classList.remove('is-hidden');
      }
      lastY = y;
    }, { passive: true });
  }

  /* ── Page load animation stagger ───────────────────────────────────────── */
  function initPageStagger() {
    const targets = document.querySelectorAll(
      '.ag-section__title, .ag-section__eyebrow, .ag-section__sub, .ag-hero__content'
    );
    targets.forEach((el, i) => {
      el.style.transitionDelay = `${i * 0.05}s`;
    });
  }

  /* ── Init everything on DOM ready ──────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', () => {
    initScrollAnimations();
    initWishlistBtns();
    initLazyImages();
    initAnnBarScroll();
    initPageStagger();
    fetchCartCount();
  });

})();
